import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import { useDispatch, useSelector } from 'react-redux';
import LocalizedStrings from 'react-localization';
import LOCALIZATIONS from './localizations';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WalletIcon from '@mui/icons-material/Wallet';
import LanIcon from '@mui/icons-material/Lan';
import { blue } from '@mui/material/colors';
import './style.css';
import { MODAL_TOGGLE } from '../../redux/actions/app';
import { useSnackbar } from 'notistack';
import { ID } from './config';
import FormHelperText from '@mui/material/FormHelperText';
import { mintContract, dispatchErr, validNetwork } from '../../util/metamask';
import CONFIG from '../../config';

const Mint = () => {

    const board = useSelector( state => state.board ),
    dispatch = useDispatch(),
    { enqueueSnackbar } = useSnackbar(),
    labels = useMemo( () => new LocalizedStrings( LOCALIZATIONS ), []),
    { locale, modal } = useSelector( state => state.app ),
    [ loading, setLoading ] = useState( false ),
    { accounts, network } = useSelector( state => state.eth ),
    [ invalidNetwork, setInvalidNetwork ] = useState( false ),
    open = Boolean( modal === ID );

    /**
     * @name cancel
     * @function
     * @description Hides the modal
     */
    const cancel = () => {
        dispatch({ type: MODAL_TOGGLE });
    }

    /**
     * @name startMint
     * @function
     * @description Create the token
     */
    const startMint = async () => {

        setLoading( true );
        const tempBoard = { ...board };
        delete tempBoard.isNew;
        delete tempBoard.currentNode;
        delete tempBoard.nodeContextMenu;
        delete tempBoard.magnifications;

        const mint = new Promise( mintContract({ board: tempBoard, locale }) );

        mint
            .then( res => {
                setLoading( false );
                enqueueSnackbar(
                    labels.formatString(
                        labels.successMint,
                        <>&nbsp;<Link target="_blank" href={ CONFIG.etherscan.replace( '{hash}', res.transactionHash ) }>{ labels.etherscan }</Link></>
                    ),
                    { variant: 'success' }
                );
                cancel();
            })
            .catch( err => {
                setLoading( false );
                console.error( err );
                dispatchErr( err, enqueueSnackbar, labels );
            });

    }

    // update locale
    useEffect(() => {
        if( locale !== labels.getLanguage() ) labels.setLanguage( locale )
    }, [ labels, locale ]);

    // make sure they're using the correct network
    useEffect(() => {

        if( open ){
            const valid = validNetwork( network );
            setInvalidNetwork( !valid );
        }

    }, [ open, network, setInvalidNetwork ]);

    return (
        <Dialog open={ open } onClose={ cancel }>
            <DialogTitle className="coe__mint">
                { 
                    labels.formatString(
                        labels.title,
                        board.name || ''
                    )
                }
            </DialogTitle>
            <DialogContent>
                <List>
                    <ListItem>
                        <ListItemAvatar>
                            <Avatar sx={{ bgcolor: blue[ 500 ] }}>
                                <LanIcon />
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                            primary={ labels.network } 
                            secondary={
                                labels.formatString(
                                    labels[ network.toString() ],
                                    labels.testNetwork
                                )
                            }/>
                    </ListItem>
                    {
                        invalidNetwork ?
                            <ListItem>
                                <FormHelperText error>
                                    { 
                                        labels.formatString(
                                            labels.wrongNetwork,
                                            labels.testNetwork
                                        ) 
                                    }
                                </FormHelperText>
                            </ListItem> :
                            null
                    }
                    <ListItem>
                        <ListItemAvatar>
                            <Avatar sx={{ bgcolor: blue[ 500 ] }}>
                                <WalletIcon />
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText>
                            <ListItemText 
                                primary={ labels.account } 
                                secondary={ accounts[ 0 ] }/>
                        </ListItemText>
                    </ListItem>
                </List>
                <Collapse in={ loading }>
                    <Alert severity="info">
                        { labels.mintingTakesTime }
                    </Alert> 
                </Collapse>
            </DialogContent>
            <DialogActions>
                <Button aria-label={ labels.confirm } disabled={ invalidNetwork || loading } onClick={ startMint } variant="contained" startIcon={ 
                    loading ? <CircularProgress size="1em" /> : <SettingsSuggestIcon /> }>
                    { labels.confirm }
                </Button>
                <Button aria-label={ labels.cancel } disabled={ loading } variant="outlined" onClick={ cancel } startIcon={ <ArrowForwardIcon /> }>
                    { labels.cancel }
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default Mint;